import { VolumeUnit, WeightUnit, UnitConversionResult } from '../types/shoppingList'

// Base conversion rates to metric (ml for volume, g for weight)
const VOLUME_TO_ML: Record<VolumeUnit, number> = {
  'ml': 1,
  'l': 1000,
  'liter': 1000,
  'liters': 1000,
  'tsp': 4.929,
  'teaspoon': 4.929,
  'teaspoons': 4.929,
  'tbsp': 14.787,
  'tablespoon': 14.787,
  'tablespoons': 14.787,
  'cup': 236.588,
  'cups': 236.588,
  'pint': 473.176,
  'pints': 473.176,
  'quart': 946.353,
  'quarts': 946.353,
  'gallon': 3785.41,
  'gallons': 3785.41,
  'fl oz': 29.5735,
  'fluid ounce': 29.5735,
  'fluid ounces': 29.5735,
}

const WEIGHT_TO_GRAMS: Record<WeightUnit, number> = {
  'g': 1,
  'gram': 1,
  'grams': 1,
  'kg': 1000,
  'kilogram': 1000,
  'kilograms': 1000,
  'oz': 28.3495,
  'ounce': 28.3495,
  'ounces': 28.3495,
  'lb': 453.592,
  'pound': 453.592,
  'pounds': 453.592,
}

// Common ingredient volume-to-weight conversions (ml to grams)
const INGREDIENT_DENSITY: Record<string, number> = {
  // Liquids (close to water density)
  'water': 1,
  'milk': 1.03,
  'broth': 1,
  'chicken broth': 1,
  'vegetable broth': 1,
  'stock': 1,
  'wine': 0.99,
  'beer': 1.01,
  
  // Oils and fats
  'oil': 0.92,
  'olive oil': 0.92,
  'vegetable oil': 0.92,
  'butter': 0.91,
  'coconut oil': 0.92,
  
  // Dairy
  'heavy cream': 0.99,
  'cream': 0.99,
  'sour cream': 0.96,
  'yogurt': 1.04,
  
  // Sweeteners
  'honey': 1.42,
  'maple syrup': 1.32,
  'corn syrup': 1.48,
  
  // Dry ingredients (grams per cup)
  'flour': 120, // g per cup
  'all-purpose flour': 120,
  'bread flour': 127,
  'sugar': 200,
  'brown sugar': 213,
  'powdered sugar': 120,
  'rice': 195,
  'white rice': 195,
  'brown rice': 195,
  'oats': 90,
  'rolled oats': 90,
  'quinoa': 173,
  'pasta': 100, // varies by shape
  
  // Default fallback for unknown ingredients
  'default': 1,
}

/**
 * Normalize unit names to handle variations
 */
function normalizeUnit(unit: string): string {
  const normalized = unit.toLowerCase().trim()
  
  // Handle common abbreviations and plurals
  const unitMappings: Record<string, string> = {
    'tsp.': 'tsp',
    'tbsp.': 'tbsp',
    'c': 'cup',
    'c.': 'cup',
    'pt': 'pint',
    'qt': 'quart',
    'gal': 'gallon',
    'fl. oz': 'fl oz',
    'floz': 'fl oz',
    'fluid oz': 'fl oz',
    'lbs': 'lb',
    'lbs.': 'lb',
    'oz.': 'oz',
    'g.': 'g',
    'kg.': 'kg',
    'l.': 'l',
    'ml.': 'ml',
    
    // Handle plurals
    'tsps': 'tsp',
    'tbsps': 'tbsp',
  }
  
  return unitMappings[normalized] || normalized
}

/**
 * Check if a unit is a volume unit
 */
function isVolumeUnit(unit: string): boolean {
  const normalized = normalizeUnit(unit)
  return normalized in VOLUME_TO_ML
}

/**
 * Check if a unit is a weight unit
 */
function isWeightUnit(unit: string): boolean {
  const normalized = normalizeUnit(unit)
  return normalized in WEIGHT_TO_GRAMS
}

/**
 * Check if a unit is a count unit
 */
function isCountUnit(unit: string): boolean {
  const countUnits = [
    'item', 'items', 'piece', 'pieces', 'slice', 'slices',
    'clove', 'cloves', 'head', 'heads', 'bunch', 'bunches',
    'package', 'packages', 'can', 'cans', 'jar', 'jars',
    'bottle', 'bottles'
  ]
  return countUnits.includes(normalizeUnit(unit))
}

/**
 * Convert volume units to a common base (ml)
 */
function convertVolumeToMl(amount: number, unit: string): number | null {
  const normalized = normalizeUnit(unit)
  const conversionRate = VOLUME_TO_ML[normalized as VolumeUnit]
  return conversionRate ? amount * conversionRate : null
}

/**
 * Convert weight units to a common base (grams)
 */
function convertWeightToGrams(amount: number, unit: string): number | null {
  const normalized = normalizeUnit(unit)
  const conversionRate = WEIGHT_TO_GRAMS[normalized as WeightUnit]
  return conversionRate ? amount * conversionRate : null
}

/**
 * Convert ml back to a preferred volume unit
 * @deprecated Kept for potential future use
 */
// @ts-expect-error - Unused function kept for potential future use
function convertMlToVolume(ml: number, targetUnit: string): number {
  const normalized = normalizeUnit(targetUnit)
  const conversionRate = VOLUME_TO_ML[normalized as VolumeUnit]
  return conversionRate ? ml / conversionRate : ml
}

/**
 * Convert grams back to a preferred weight unit
 * @deprecated Kept for potential future use
 */
// @ts-expect-error - Unused function kept for potential future use
function convertGramsToWeight(grams: number, targetUnit: string): number {
  const normalized = normalizeUnit(targetUnit)
  const conversionRate = WEIGHT_TO_GRAMS[normalized as WeightUnit]
  return conversionRate ? grams / conversionRate : grams
}

/**
 * Attempt to convert volume to weight for a specific ingredient
 */
function convertVolumeToWeight(amount: number, volumeUnit: string, ingredientName: string): { amount: number; unit: string } | null {
  const mlAmount = convertVolumeToMl(amount, volumeUnit)
  if (!mlAmount) return null
  
  // Find density for this ingredient
  const ingredientKey = ingredientName.toLowerCase().trim()
  const density = INGREDIENT_DENSITY[ingredientKey] || INGREDIENT_DENSITY['default'] || 1

  // Special handling for dry ingredients measured in cups
  if (normalizeUnit(volumeUnit) === 'cup' || normalizeUnit(volumeUnit) === 'cups') {
    // For dry ingredients, use direct cup-to-gram conversions
    const cupWeight = INGREDIENT_DENSITY[ingredientKey]
    if (cupWeight && cupWeight > 10) { // If it's a cup-to-gram conversion (not density)
      return {
        amount: amount * cupWeight,
        unit: 'g'
      }
    }
  }

  // Standard density conversion
  const grams = mlAmount * density
  
  // Choose appropriate unit
  if (grams >= 1000) {
    return { amount: grams / 1000, unit: 'kg' }
  } else if (grams >= 28.35) {
    return { amount: grams / 28.35, unit: 'oz' }
  } else {
    return { amount: grams, unit: 'g' }
  }
}

/**
 * Attempt to convert weight to volume for a specific ingredient
 */
function convertWeightToVolume(amount: number, weightUnit: string, ingredientName: string): { amount: number; unit: string } | null {
  const gramsAmount = convertWeightToGrams(amount, weightUnit)
  if (!gramsAmount) return null
  
  const ingredientKey = ingredientName.toLowerCase().trim()
  const density = INGREDIENT_DENSITY[ingredientKey] || INGREDIENT_DENSITY['default'] || 1

  // Special handling for ingredients with cup-to-gram conversions
  const cupWeight = INGREDIENT_DENSITY[ingredientKey]
  if (cupWeight && cupWeight > 10) {
    const cups = gramsAmount / cupWeight
    if (cups >= 1) {
      return { amount: cups, unit: 'cup' }
    } else if (cups >= 0.25) {
      return { amount: cups, unit: 'cup' }
    } else {
      return { amount: cups * 16, unit: 'tbsp' }
    }
  }

  // Standard density conversion
  const ml = gramsAmount / density
  
  // Choose appropriate volume unit
  if (ml >= 946) {
    return { amount: ml / 946, unit: 'quart' }
  } else if (ml >= 237) {
    return { amount: ml / 237, unit: 'cup' }
  } else if (ml >= 15) {
    return { amount: ml / 15, unit: 'tbsp' }
  } else {
    return { amount: ml / 5, unit: 'tsp' }
  }
}

/**
 * Choose the best target unit for displaying consolidated amounts
 */
function chooseBestUnit(amount: number, unit: string): { amount: number; unit: string } {
  const normalized = normalizeUnit(unit)
  
  if (isVolumeUnit(normalized)) {
    const ml = convertVolumeToMl(amount, normalized)
    if (!ml) return { amount, unit }
    
    // Choose most appropriate volume unit
    if (ml >= 3785) return { amount: ml / 3785, unit: 'gallon' }
    if (ml >= 946) return { amount: ml / 946, unit: 'quart' }
    if (ml >= 237) return { amount: ml / 237, unit: 'cup' }
    if (ml >= 15) return { amount: ml / 15, unit: 'tbsp' }
    if (ml >= 5) return { amount: ml / 5, unit: 'tsp' }
    return { amount: ml, unit: 'ml' }
  }
  
  if (isWeightUnit(normalized)) {
    const grams = convertWeightToGrams(amount, normalized)
    if (!grams) return { amount, unit }
    
    // Choose most appropriate weight unit
    if (grams >= 1000) return { amount: grams / 1000, unit: 'kg' }
    if (grams >= 454) return { amount: grams / 454, unit: 'lb' }
    if (grams >= 28.35) return { amount: grams / 28.35, unit: 'oz' }
    return { amount: grams, unit: 'g' }
  }
  
  return { amount, unit }
}

/**
 * Main function to combine two ingredient amounts with unit conversion
 */
export function combineIngredientAmounts(
  amount1: number,
  unit1: string,
  amount2: number,
  unit2: string,
  ingredientName: string
): UnitConversionResult {
  const normalizedUnit1 = normalizeUnit(unit1)
  const normalizedUnit2 = normalizeUnit(unit2)
  
  // If units are the same, simple addition
  if (normalizedUnit1 === normalizedUnit2) {
    const totalAmount = amount1 + amount2
    const bestUnit = chooseBestUnit(totalAmount, normalizedUnit1)
    
    return {
      success: true,
      amount: bestUnit.amount,
      unit: bestUnit.unit,
      originalAmount1: amount1,
      originalUnit1: unit1,
      originalAmount2: amount2,
      originalUnit2: unit2,
    }
  }
  
  // Try volume-to-volume conversion
  if (isVolumeUnit(normalizedUnit1) && isVolumeUnit(normalizedUnit2)) {
    const ml1 = convertVolumeToMl(amount1, normalizedUnit1)
    const ml2 = convertVolumeToMl(amount2, normalizedUnit2)
    
    if (ml1 && ml2) {
      const totalMl = ml1 + ml2
      const bestUnit = chooseBestUnit(totalMl, 'ml')
      
      return {
        success: true,
        amount: bestUnit.amount,
        unit: bestUnit.unit,
        originalAmount1: amount1,
        originalUnit1: unit1,
        originalAmount2: amount2,
        originalUnit2: unit2,
        conversionNote: `Converted ${unit1} and ${unit2} to common volume unit`
      }
    }
  }
  
  // Try weight-to-weight conversion
  if (isWeightUnit(normalizedUnit1) && isWeightUnit(normalizedUnit2)) {
    const g1 = convertWeightToGrams(amount1, normalizedUnit1)
    const g2 = convertWeightToGrams(amount2, normalizedUnit2)
    
    if (g1 && g2) {
      const totalGrams = g1 + g2
      const bestUnit = chooseBestUnit(totalGrams, 'g')
      
      return {
        success: true,
        amount: bestUnit.amount,
        unit: bestUnit.unit,
        originalAmount1: amount1,
        originalUnit1: unit1,
        originalAmount2: amount2,
        originalUnit2: unit2,
        conversionNote: `Converted ${unit1} and ${unit2} to common weight unit`
      }
    }
  }
  
  // Try volume-to-weight conversion
  if (isVolumeUnit(normalizedUnit1) && isWeightUnit(normalizedUnit2)) {
    const converted1 = convertVolumeToWeight(amount1, normalizedUnit1, ingredientName)
    const grams2 = convertWeightToGrams(amount2, normalizedUnit2)
    
    if (converted1 && grams2) {
      const convertedGrams1 = convertWeightToGrams(converted1.amount, converted1.unit)
      if (convertedGrams1) {
        const totalGrams = convertedGrams1 + grams2
        const bestUnit = chooseBestUnit(totalGrams, 'g')
        
        return {
          success: true,
          amount: bestUnit.amount,
          unit: bestUnit.unit,
          originalAmount1: amount1,
          originalUnit1: unit1,
          originalAmount2: amount2,
          originalUnit2: unit2,
          conversionNote: `Converted volume to weight for ${ingredientName}`
        }
      }
    }
  }
  
  // Try weight-to-volume conversion
  if (isWeightUnit(normalizedUnit1) && isVolumeUnit(normalizedUnit2)) {
    const converted1 = convertWeightToVolume(amount1, normalizedUnit1, ingredientName)
    const ml2 = convertVolumeToMl(amount2, normalizedUnit2)
    
    if (converted1 && ml2) {
      const convertedMl1 = convertVolumeToMl(converted1.amount, converted1.unit)
      if (convertedMl1) {
        const totalMl = convertedMl1 + ml2
        const bestUnit = chooseBestUnit(totalMl, 'ml')
        
        return {
          success: true,
          amount: bestUnit.amount,
          unit: bestUnit.unit,
          originalAmount1: amount1,
          originalUnit1: unit1,
          originalAmount2: amount2,
          originalUnit2: unit2,
          conversionNote: `Converted weight to volume for ${ingredientName}`
        }
      }
    }
  }
  
  // Count units - only combine if identical
  if (isCountUnit(normalizedUnit1) && isCountUnit(normalizedUnit2) && normalizedUnit1 === normalizedUnit2) {
    return {
      success: true,
      amount: amount1 + amount2,
      unit: normalizedUnit1,
      originalAmount1: amount1,
      originalUnit1: unit1,
      originalAmount2: amount2,
      originalUnit2: unit2,
    }
  }
  
  // If no conversion possible, return failure
  return {
    success: false,
    amount: amount1,
    unit: unit1,
    originalAmount1: amount1,
    originalUnit1: unit1,
    originalAmount2: amount2,
    originalUnit2: unit2,
    conversionNote: `Cannot combine ${unit1} and ${unit2} for ${ingredientName}`
  }
}

/**
 * Format amount for display (handle fractions and decimals nicely)
 */
export function formatAmount(amount: number): string {
  // Round to avoid floating point errors
  const rounded = Math.round(amount * 1000) / 1000
  
  // Handle common fractions
  const commonFractions: Record<number, string> = {
    0.125: '1/8',
    0.25: '1/4',
    0.333: '1/3',
    0.5: '1/2',
    0.667: '2/3',
    0.75: '3/4',
  }
  
  const fraction = Object.entries(commonFractions).find(([decimal]) => 
    Math.abs(rounded - parseFloat(decimal)) < 0.01
  )
  
  if (fraction) {
    return fraction[1]
  }
  
  // Handle mixed numbers (like 2 1/2)
  if (rounded > 1) {
    const wholeNumber = Math.floor(rounded)
    const fractionalPart = rounded - wholeNumber
    
    const mixedFraction = Object.entries(commonFractions).find(([decimal]) => 
      Math.abs(fractionalPart - parseFloat(decimal)) < 0.01
    )
    
    if (mixedFraction) {
      return `${wholeNumber} ${mixedFraction[1]}`
    }
  }
  
  // Default to decimal, avoiding unnecessary decimals
  if (rounded === Math.floor(rounded)) {
    return rounded.toString()
  } else if (rounded < 10) {
    return rounded.toFixed(2).replace(/\.?0+$/, '')
  } else {
    return rounded.toFixed(1).replace(/\.?0+$/, '')
  }
}